import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { format } from 'date-fns';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';

import DataTable from './DataTable';
import { valueFormatter } from './utils';
import { average } from '../../../utils/helpers/numbers';
import { DataItemType } from '../Graph/types';
import ValueDifference from '../ValueDifference';
import { calculateValueDifference, formatValueDifference, shouldDisplayValueDifference } from '../valueDifferenceUtils';
import { useWidgetContext } from '../Widget/WidgetContext';

interface Props {
  data: any;
}

const DataTableRange = ({ data }: Props) => {
  const { type, setCSVData, filters } = useWidgetContext();
  const { community } = useCommunityViewContext();
  const valueDisplay = filters?.valueDisplay;
  const comparisonTypeEnabled = filters?.comparisonTypeEnabled;
  const showDifference = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);
  const [tableData, setTableData] = useState<any>([]);
  const [quarterList, setQuarterList] = useState<string[]>([]);

  useEffect(() => {
    // @ts-ignore
    const enhancedData = data.map((item: DataItemType) => ({
      ...item,
      quarter: format(new Date(item.date ?? ''), 'qqq yyyy'),
    }));

    const dataQuarterList = Array.from(new Set(enhancedData?.map((item: any) => item.quarter))).sort(
      (quarterA: any, quarterB: any) => (new Date(quarterA) > new Date(quarterB) ? -1 : 1)
    );

    setQuarterList(dataQuarterList as string[]);

    const aggregatedValues = {};

    enhancedData.forEach((item: any) => {
      const quarterKey = item.quarter;

      // Initialize counts object for the community if not exists
      if (!(item.community_name in aggregatedValues)) {
        // @ts-ignore
        aggregatedValues[item.community_name] = {};
      }

      // @ts-ignore
      const currentCommunity = aggregatedValues[item.community_name];

      if (!(quarterKey in currentCommunity)) {
        // @ts-ignore
        aggregatedValues[item.community_name][quarterKey] = [item.value];
      } else {
        // @ts-ignore
        aggregatedValues[item.community_name][quarterKey].push(item.value);
      }
    });

    const adjustedData = Object.entries(aggregatedValues).map(([communityName, quarterValues]) => ({
      communityName,
      communityType: data.find((item: DataItemType) => item.community_name === communityName)?.community_type,
      ...(quarterValues as object),
    }));

    // Build CSV with differences when compare is enabled
    const csvExportData = [['COMMUNITY', ...(dataQuarterList as string[])]];
    const csvCommunityAverages: Record<string, number | null> = {};

    if (showDifference) {
      const communityEntry = adjustedData.find(
        (row: any) => row.communityType == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
      );
      if (communityEntry) {
        (dataQuarterList as string[]).forEach((quarter: string) => {
          const values = (communityEntry as any)[quarter];
          csvCommunityAverages[quarter] = Array.isArray(values) && values.length ? average(values) : null;
        });
      }
    }

    adjustedData.forEach((row: any) => {
      const communityQuarterValues = (dataQuarterList as string[]).map((quarter: string) => {
        if (!row[quarter]) return '';
        const cellAvg = average(row[quarter]);
        const formatted = valueFormatter(cellAvg, type, true, undefined, community);

        if (showDifference && row.communityType !== COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY) {
          const baseValue = csvCommunityAverages[quarter] ?? null;
          const diff = calculateValueDifference(baseValue, cellAvg, valueDisplay, type);
          const formattedDiff = formatValueDifference(diff, type, valueDisplay, community);
          if (formattedDiff) return `${formatted} ${formattedDiff}`;
        }

        return formatted;
      });
      csvExportData.push([row.communityName, ...communityQuarterValues]);
    });

    setCSVData(csvExportData);
    setTableData(adjustedData);
  }, [community, data, showDifference, valueDisplay, type, setCSVData]);

  const communityQuarterAverages = useMemo(() => {
    const communityRow = tableData.find((row: any) => row.communityType == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY);
    if (!communityRow || !quarterList.length) {
      return {};
    }

    return quarterList.reduce(
      (acc, quarter) => {
        const values = communityRow[quarter];
        acc[quarter] = Array.isArray(values) && values.length ? average(values) : null;
        return acc;
      },
      {} as Record<string, number | null>
    );
  }, [tableData, quarterList]);

  const quarterColumnWidth = 65;
  const quarterColumns = quarterList.map((quarter) => ({
    title: quarter,
    dataIndex: quarter,
    align: 'center' as const,
    width: quarterColumnWidth,
    onHeaderCell: () => ({ style: { minWidth: quarterColumnWidth } }),
    onCell: () => ({ style: { minWidth: quarterColumnWidth } }),
    render: (value: any, record: any) => {
      if (!value) {
        return <span>-</span>;
      }

      const cellAverage = average(value);
      const baseValue = communityQuarterAverages[quarter] ?? null;
      const difference =
        showDifference && record.communityType !== COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
          ? calculateValueDifference(baseValue ?? null, cellAverage, valueDisplay, type)
          : null;

      return (
        <span>
          {valueFormatter(cellAverage, type, true, undefined, community)}
          <ValueDifference difference={difference} widgetType={type} valueDisplay={valueDisplay} />
        </span>
      );
    },
  }));

  const columns = [
    {
      title: 'COMMUNITY',
      dataIndex: 'communityName',
      width: '250px',
      align: 'left' as const,
      render: (value: any, record: any) => (
        <span
          className={classNames({ highlighted: record.communityType == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY })}
        >
          {value}
        </span>
      ),
    },
    ...quarterColumns,
  ];

  return (
    <DataTable
      columns={columns}
      dataSource={tableData}
      scroll={{ x: quarterList.length * quarterColumnWidth + 250, y: 'max-content' }}
      pagination={false}
      tableLayout="fixed"
      rowKey={(record: any) => record?.communityName}
    />
  );
};

export default DataTableRange;
